import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Alert, Box, Button, Chip, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useGetRubricByIdQuery, useUpdateRubricMutation } from './rubricsApi';
import RubricGrid from './components/RubricGrid';
import HolisticEditor from './components/HolisticEditor';
import SinglePointEditor from './components/SinglePointEditor';

import type { RubricContent } from '@/shared/types/rubric';
import { useObjectPermissionState } from '@/shared/hooks/useCanEdit';
import { useAppSelector } from '@/shared/hooks/redux';
import { PageSpinner } from '@/shared/components/PageSpinner';
import { EditableText } from '@/shared/components/EditableText';

const AUTOSAVE_DELAY = 900;

function typeLabel(content: RubricContent | null): string {
  if (!content) return 'Empty';
  switch (content.rubricType) {
    case 'analytic':
      return 'Analytic';
    case 'holistic':
      return 'Holistic';
    case 'single-point':
      return 'Single-Point';
    default:
      return 'Unknown';
  }
}

function typeColor(content: RubricContent | null): 'primary' | 'secondary' | 'success' | 'default' {
  if (!content) return 'default';
  switch (content.rubricType) {
    case 'analytic':
      return 'primary';
    case 'holistic':
      return 'secondary';
    case 'single-point':
      return 'success';
    default:
      return 'default';
  }
}

const RubricEditorPage: React.FC = () => {
  const { rubricId } = useParams<{ rubricId: string }>();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  const { data: rubric, isLoading, error } = useGetRubricByIdQuery(rubricId!, { skip: !rubricId });
  const isOwner = Boolean(rubric && user && rubric.ownerId === user.id);
  const rubricPermission = useObjectPermissionState('rubric', rubricId, 'editor', {
    fallbackAllowed: isOwner,
  });
  const canEdit = rubricPermission.isAllowed;
  const [updateRubric] = useUpdateRubricMutation();

  // Local working copy for debounced saves
  const [localTitle, setLocalTitle] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const [localContent, setLocalContent] = useState<RubricContent | null>(null);
  const [dirty, setDirty] = useState(false);
  const initializedRef = useRef(false);
  const changeCountRef = useRef(0);

  // Sync server → local on load / external update (only when not dirty)
  useEffect(() => {
    if (!rubric) return;
    if (initializedRef.current && dirty) return;
    setLocalTitle(rubric.title);
    setLocalDescription(rubric.description ?? '');
    setLocalContent(rubric.content);
    initializedRef.current = true;
  }, [rubric, dirty]);

  // Debounced auto-save
  useEffect(() => {
    if (!canEdit || !dirty || !rubricId) return;
    const countAtSchedule = changeCountRef.current;
    const timer = setTimeout(() => {
      const payload: Record<string, unknown> = { id: rubricId };
      if (localTitle !== rubric?.title) payload.title = localTitle;
      if (localDescription !== (rubric?.description ?? ''))
        payload.description = localDescription || null;
      if (localContent !== rubric?.content) payload.content = localContent;
      if (Object.keys(payload).length > 1) {
        updateRubric(payload as any)
          .unwrap()
          .then(() => {
            // Only clear dirty if no new changes arrived while saving
            if (changeCountRef.current === countAtSchedule) {
              setDirty(false);
            }
          })
          .catch(() => {
            /* keep dirty — autosave will retry on next change */
          });
      } else {
        setDirty(false);
      }
    }, AUTOSAVE_DELAY);
    return () => clearTimeout(timer);
  }, [dirty, rubricId, localTitle, localDescription, localContent, rubric, updateRubric]);

  // Warn on unload with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const handleTitleChange = useCallback(async (title: string) => {
    setLocalTitle(title);
    changeCountRef.current++;
    setDirty(true);
  }, []);

  const handleDescriptionChange = useCallback(async (desc: string) => {
    setLocalDescription(desc);
    changeCountRef.current++;
    setDirty(true);
  }, []);

  const handleContentChange = useCallback((content: RubricContent) => {
    setLocalContent(content);
    changeCountRef.current++;
    setDirty(true);
  }, []);

  if (isLoading) return <PageSpinner />;
  if (error || !rubric) {
    return (
      <Box sx={{ py: 6, px: { xs: 2, md: 4 }, textAlign: 'center' }}>
        <Alert severity={!user ? 'warning' : 'error'}>
          {!user ? (
            <>
              This content may be private. <Link to="/login">Sign in</Link> to access it if you have
              permission.
            </>
          ) : (
            'Rubric not found'
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {/* Header — matches LessonPage / UnitPage layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          columnGap: 2,
          rowGap: 1,
          alignItems: 'start',
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Chip
              label={typeLabel(localContent)}
              color={typeColor(localContent)}
              size="small"
              variant="outlined"
            />
          </Box>
          <EditableText
            value={localTitle}
            onSave={handleTitleChange}
            canEdit={canEdit}
            typographyProps={{ variant: 'h2', component: 'h1' }}
            textFieldProps={{
              sx: {
                '& .MuiInputBase-input': {
                  fontSize: '2.125rem',
                  fontWeight: 300,
                  lineHeight: 1.235,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/curriculum/rubrics')}
          >
            My Rubrics
          </Button>
        </Box>
        <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <EditableText
            value={localDescription}
            onSave={handleDescriptionChange}
            canEdit={canEdit}
            allowEmpty
            multiline
            typographyProps={{ variant: 'subtitle1', color: 'text.secondary' }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Editor body */}
      {localContent?.rubricType === 'analytic' && (
        <RubricGrid
          content={localContent}
          canEdit={canEdit}
          ownerId={rubric.ownerId}
          onChange={handleContentChange}
        />
      )}

      {localContent?.rubricType === 'holistic' && (
        <HolisticEditor content={localContent} canEdit={canEdit} onChange={handleContentChange} />
      )}

      {localContent?.rubricType === 'single-point' && (
        <SinglePointEditor
          content={localContent}
          canEdit={canEdit}
          ownerId={rubric.ownerId}
          onChange={handleContentChange}
        />
      )}
    </Box>
  );
};

export default RubricEditorPage;
